import React, { useState, useEffect, useCallback } from 'react';
import { Play, Clock, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.js';
import { useSubscription } from '../contexts/SubscriptionContext.js';
import { useProgress } from '../contexts/ProgressContext.js';
import cloudflareStreamService from '../services/cloudflareStreamService.js';
import SafetyNotice from './common/SafetyNotice.js';

const WorkoutOfTheWeek = ({ monthlyPackage, onVideoPlay, initialWeek }) => {
  const { user } = useAuth();
  const { subscription } = useSubscription();
  const { completeWorkout, workoutHistory } = useProgress();
  const [selectedWeek, setSelectedWeek] = useState(initialWeek || 1);
  const [videoUrls, setVideoUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const [completedWeeks, setCompletedWeeks] = useState([]);

  // Debug logging
  console.log('WorkoutOfTheWeek component received monthlyPackage:', monthlyPackage);
  console.log('WorkoutOfTheWeek workoutOfTheWeek:', monthlyPackage?.workoutOfTheWeek);
  console.log('WorkoutOfTheWeek dailyChallenge:', monthlyPackage?.dailyChallenge);
  console.log('WorkoutOfTheWeek title:', monthlyPackage?.title);
  console.log('WorkoutOfTheWeek theme:', monthlyPackage?.theme);

  // Update selected week when initialWeek changes (from URL hash)
  useEffect(() => {
    if (initialWeek) {
      setSelectedWeek(initialWeek);
    }
  }, [initialWeek]);

  const userTier = subscription?.tier || 'basic';
  const hasAccess = monthlyPackage?.availableTiers?.includes(userTier);

  // Get the next uncompleted week based on user progress
  const getNextUncompletedWeek = useCallback(() => {
    // Start with week 1 if no weeks are completed
    if (completedWeeks.length === 0) {
      return 1;
    }
    
    // Find the next uncompleted week
    for (let week = 1; week <= 4; week++) {
      if (!completedWeeks.includes(week)) {
        return week;
      }
    }
    
    // If all weeks are completed, return week 4
    return 4;
  }, [completedWeeks]);

  // Load user's completed weeks from progress
  useEffect(() => {
    console.log('Loading completed weeks - user:', user, 'workoutHistory:', workoutHistory);
    console.log('MonthlyPackage subscriptionMonth:', monthlyPackage?.subscriptionMonth);
    
    if (user && workoutHistory && Array.isArray(workoutHistory) && monthlyPackage?.subscriptionMonth) {
      try {
        const month = monthlyPackage.subscriptionMonth;
        // Look for WOW workouts in the history that match this specific month
        const wowWeeks = workoutHistory
          .filter(workout => {
            if (!workout.id) return false;
            
            // For month 1, match both old format (wow-X) and new format (month1-wow-X)
            // For months 2+, only match new format (monthX-wow-X)
            if (month === 1) {
              // Month 1: match both formats
              const hasWow = workout.id.includes(`month1-wow-`) || 
                            (workout.id.includes('wow-') && !workout.id.includes('month'));
              console.log('Checking workout:', workout.id, 'for month 1, matches:', hasWow);
              return hasWow;
            } else {
              // Months 2+: only match new format with specific month
              const hasWow = workout.id.includes(`month${month}-wow-`);
              console.log('Checking workout:', workout.id, 'for month', month, 'matches:', hasWow);
              return hasWow;
            }
          })
          .map(workout => {
            // Extract week number from workout ID
            // New format: "month3-wow-1" -> 1
            // Old format (month 1 only): "wow-1" -> 1
            let weekMatch;
            if (workout.id.includes(`month${month}-wow-`)) {
              weekMatch = workout.id.match(new RegExp(`month${month}-wow-(\\d+)`));
            } else if (month === 1 && workout.id.includes('wow-') && !workout.id.includes('month')) {
              // Legacy format - only for month 1
              weekMatch = workout.id.match(/wow-(\d+)/);
            }
            const week = weekMatch ? parseInt(weekMatch[1]) : null;
            console.log('Extracted week from', workout.id, ':', week);
            return week;
          })
          .filter(week => week !== null);
        
        console.log('Found WOW weeks for month', month, ':', wowWeeks);
        setCompletedWeeks(wowWeeks);
      } catch (error) {
        console.error('Error loading completed weeks:', error);
        setCompletedWeeks([]);
      }
    } else {
      console.log('No user, workoutHistory, or subscriptionMonth, starting with empty array');
      // Start with empty array if no progress data
      setCompletedWeeks([]);
    }
  }, [user, workoutHistory, monthlyPackage?.subscriptionMonth]);

  useEffect(() => {
    // Only auto-select next week if no initialWeek was provided (not navigating from favorites)
    if (monthlyPackage?.workoutOfTheWeek && !initialWeek) {
      const nextWeek = getNextUncompletedWeek();
      setSelectedWeek(nextWeek);
    }
  }, [monthlyPackage, completedWeeks, initialWeek, getNextUncompletedWeek]);

  const handleWeekSelect = (week) => {
    setSelectedWeek(week);
  };

  const handlePlayVideo = async (workout) => {
    if (!hasAccess) {
      alert('Please upgrade to Premium or Elite to access workout videos.');
      return;
    }

    try {
      setLoading(true);
      
      // Check if we already have the video URL
      if (videoUrls[workout.cloudflareStreamId]) {
        onVideoPlay(videoUrls[workout.cloudflareStreamId], workout);
        return;
      }

      // Generate signed URL for the video
      const videoUrl = await cloudflareStreamService.getPlayerUrl(
        workout.cloudflareStreamId,
        userTier
      );

      // Store the URL for future use
      setVideoUrls(prev => ({
        ...prev,
        [workout.cloudflareStreamId]: videoUrl
      }));

      onVideoPlay(videoUrl, workout);
    } catch (error) {
      console.error('Error loading video:', error);
      alert('Failed to load video. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteWeek = async (week) => {
    console.log('handleCompleteWeek called for week:', week);
    console.log('Current completedWeeks:', completedWeeks);
    
    if (!completedWeeks.includes(week)) {
      try {
        const selectedWorkout = monthlyPackage.workoutOfTheWeek.find(w => w.week === week);
        if (selectedWorkout && user && completeWorkout) {
          // Convert duration from seconds to minutes, ensure minimum of 1 minute
          const durationMinutes = Math.max(1, Math.round(selectedWorkout.duration / 60));
          
          // Get month number from monthlyPackage
          const month = monthlyPackage.subscriptionMonth || 1;
          
          // Create workout completion data with month identifier
          const workoutId = `month${month}-wow-${week}`;
          const exercisesCompleted = [{
            exerciseId: `month${month}-wow-${week}`,
            name: selectedWorkout.title || `WOW Week ${week}`,
            reps: 1,
            duration: durationMinutes
          }];
          
          console.log('WOW completion data:', {
            workoutId,
            exercisesCompleted,
            durationMinutes,
            originalDuration: selectedWorkout.duration
          });
          
          // Save to progress system using completeWorkout
          const success = await completeWorkout(workoutId, exercisesCompleted, durationMinutes);
          
          if (success) {
            setCompletedWeeks(prev => {
              const newCompleted = [...prev, week];
              console.log('Updated completedWeeks to:', newCompleted);
              return newCompleted;
            });
            console.log(`Week ${week} completed and saved to progress!`);
          } else {
            console.error('Failed to save WOW progress');
            // Still mark as completed locally
            setCompletedWeeks(prev => {
              const newCompleted = [...prev, week];
              console.log('Updated completedWeeks to (fallback):', newCompleted);
              return newCompleted;
            });
          }
        } else {
          // Fallback if completeWorkout is not available
          setCompletedWeeks(prev => {
            const newCompleted = [...prev, week];
            console.log('Updated completedWeeks to (no progress):', newCompleted);
            return newCompleted;
          });
          console.log(`Week ${week} completed!`);
        }
      } catch (error) {
        console.error('Error completing WOW week:', error);
        // Still mark as completed locally even if progress save fails
        setCompletedWeeks(prev => {
          const newCompleted = [...prev, week];
          console.log('Updated completedWeeks to (error fallback):', newCompleted);
          return newCompleted;
        });
      }
    } else {
      console.log('Week', week, 'is already completed');
    }
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!monthlyPackage?.workoutOfTheWeek?.length) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-6xl mb-4">🏋️‍♀️</div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          No Workout of the Week Available
        </h3>
        <p className="text-gray-600">
          Check back soon for new weekly workouts!
        </p>
      </div>
    );
  }

  const selectedWorkout = monthlyPackage.workoutOfTheWeek.find(w => w.week === selectedWeek);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          ��️‍♀️ Workout of the Week
        </h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
          Four weekly workouts designed to challenge and improve your fitness. 
          Complete one workout each week to build strength, endurance, and confidence.
        </p>
        <div className="max-w-3xl mx-auto">
          <SafetyNotice />
        </div>
      </div>

      {/* Weekly Navigation */}
      <div className="flex justify-center">
        <div className="bg-white rounded-lg shadow-md p-2 flex space-x-2">
          {monthlyPackage.workoutOfTheWeek.map((workout) => (
            <button
              key={workout.week}
              onClick={() => handleWeekSelect(workout.week)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedWeek === workout.week
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center space-x-2">
                <span>Week {workout.week}</span>
                {completedWeeks.includes(workout.week) && (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                )}
                {workout.week === selectedWeek && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Selected Workout Details */}
      {selectedWorkout && (
        <div id={`wow-week-${selectedWorkout.week}`} className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Workout Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold mb-2">{selectedWorkout.title}</h3>
                <p className="text-blue-100">{selectedWorkout.description}</p>
              </div>
              <div className="text-right">
                <div className="flex items-center text-blue-100 mb-2">
                  <Clock className="w-5 h-5 mr-2" />
                  <span>{formatDuration(selectedWorkout.duration)}</span>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getDifficultyColor(selectedWorkout.difficulty)}`}>
                  {selectedWorkout.difficulty.charAt(0).toUpperCase() + selectedWorkout.difficulty.slice(1)}
                </span>
              </div>
            </div>
          </div>

          {/* Workout Content */}
          <div className="p-6">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Video Section */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Workout Video</h4>
                <div className="relative bg-gray-900 rounded-lg overflow-hidden">
                  <div className="aspect-video bg-gray-800 flex items-center justify-center">
                    {hasAccess ? (
                      <button
                        onClick={() => handlePlayVideo(selectedWorkout)}
                        disabled={loading}
                        className="flex items-center space-x-3 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
                      >
                        <Play className="w-6 h-6" />
                        <span>{loading ? 'Loading...' : 'Play Workout'}</span>
                      </button>
                    ) : (
                      <div className="text-center text-gray-400">
                        <Play className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>Upgrade to Premium to access workout videos</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Workout Info */}
              <div>
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Workout Details</h4>
                
                {/* Focus Areas */}
                {selectedWorkout.focusAreas?.length > 0 && (
                  <div className="mb-4">
                    <h5 className="font-medium text-gray-900 mb-2">Focus Areas:</h5>
                    <div className="flex flex-wrap gap-2">
                      {selectedWorkout.focusAreas.map((area, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                        >
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Equipment */}
                {selectedWorkout.equipment?.length > 0 && (
                  <div className="mb-4">
                    <h5 className="font-medium text-gray-900 mb-2">Equipment Needed:</h5>
                    <div className="flex flex-wrap gap-2">
                      {selectedWorkout.equipment.map((item, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full"
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Progress Tracking */}
                <div className="mt-6">
                  {console.log('Rendering button for week:', selectedWorkout.week, 'completedWeeks:', completedWeeks, 'isCompleted:', completedWeeks.includes(selectedWorkout.week))}
                  <button
                    onClick={() => {
                      console.log('Button clicked for week:', selectedWorkout.week, 'selectedWeek state:', selectedWeek);
                      handleCompleteWeek(selectedWorkout.week);
                    }}
                    disabled={completedWeeks.includes(selectedWorkout.week)}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                      completedWeeks.includes(selectedWorkout.week)
                        ? 'bg-green-100 text-green-800 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                  >
                    {completedWeeks.includes(selectedWorkout.week) ? (
                      <>
                        <CheckCircle className="w-5 h-5 inline mr-2" />
                        Week {selectedWorkout.week} Completed
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 inline mr-2" />
                        Mark Week {selectedWorkout.week} as Complete
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default WorkoutOfTheWeek; 